// ============================================================================
// COMMENTS SECTION COMPONENT
// Beautiful, threaded comments with internal/external toggle
// Used in WorkItemModal for commenting on work items
// Date: December 2025
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/comments-collaboration.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/comments-collaboration)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Send,
  MoreHorizontal,
  Trash2,
  Edit3,
  Lock,
  Globe,
  Mic,
  MicOff,
  Loader2,
  MessageSquare,
  Plus,
  ExternalLink,
  SquarePen,
  X,
  ListOrdered,
  AlignLeft,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useTheme } from '@/contexts/ThemeContext';
import {
  createSpeechToTextController,
  isSpeechToTextSupported,
  type SpeechToTextController,
} from '@/lib/speechToText';
import { isCapacitor } from '@/lib/platformUtils';
import { notify, confirm } from '@/lib/notifications';
import { getTimePartsSAST, getDateStrSAST } from '@/lib/sastTime';
import AppointmentCompletionRequirementOverrideCard from './AppointmentCompletionRequirementOverrideCard';
import { UserAvatar, UserIdentityLink, getUserDisplayName } from './WorkItemUserIdentity';
import {
  appendRecordedSpeechToItemizedComment,
  appendRecordedSpeechToPlainComment,
  appendItemizedCommentLine,
  appendPlainCommentLine,
  type CommentSourceReferenceKind,
  createItemizedCommentDraft,
  createPlainCommentDraft,
  extractItemizedCommentLines,
  FormattedCommentBody,
  getCommentSourceBadgeMeta,
  isItemizedCommentText,
  mergeSpeechIntoItemizedComment,
  mergeSpeechIntoPlainComment,
  serializeItemizedCommentDraft,
  serializePlainCommentDraft,
  extractMentionUserIdsFromCommentText,
  formatWorkItemCommentMentionToken,
} from './commentPresentation';
import {
  getWorkItemCommentTypeForPersistence,
  getWorkItemCommentTypeLabel,
  isBillingWorkItemComment,
  isClosingDetailsComment,
  isExternalWorkItemComment,
  resolveWorkItemCommentType,
  type WorkItemCommentType,
} from '@/lib/work-items/commentTypes';
import { hasPrivilegedCommentRole } from '@/lib/work-items/commentAccess';
import masterSync from '@/lib/masterSync';
import type { SmartTaskQueuedCommentCreate } from '@/lib/operations/smartTaskOfflineActions';
import type {
  AppointmentCompletionRequirementKey,
  AppointmentTypeAwareWorkItem,
  ConfigurableTypeOption,
} from '@/lib/work-items/appointmentTypeConfig';
import UserDocsLink from '@/components/shared/UserDocsLink';

// ============================================================================
// TYPES
// ============================================================================

interface Comment {
  id: string;
  work_item_id: string;
  client_id: number;
  user_id: number;
  user_name: string;
  user_email?: string;
  comment_text: string;
  is_internal: boolean;
  comment_type?: WorkItemCommentType | null;
  mentioned_users?: number[];
  parent_comment_id?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  user_display_name?: string;
  replies?: Comment[];
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
  source_reference_kind?: CommentSourceReferenceKind | null;
  offline_status?: 'pending' | 'failed' | null;
  offline_error?: string | null;
  offline_action_id?: string | null;
  is_edited?: boolean;
  is_itemized?: boolean;
}

interface CommentsSectionProps {
  workItemId: string;
  secondaryWorkItemId?: string;
  isActive: boolean;
  eagerLoad?: boolean;
  onCommentsChange?: (count: number) => void;
  onPublicCommentsChange?: (comments: Array<{ id: string; user_name: string; comment_text: string; created_at: string; is_internal: boolean; comment_type?: WorkItemCommentType | null; is_itemized?: boolean }>) => void;
  activityMetadata?: Record<string, unknown> | null;
  showInternalToggle?: boolean;
  defaultInternal?: boolean;
  enableExternalCommentType?: boolean;
  closingDetailsLocked?: boolean;
  closingDetailsLockedReason?: string;
  readOnly?: boolean;
  allowCreateWhenReadOnly?: boolean;
  readOnlyReason?: string;
  /**
   * `edit_comments` action permission (Actions tab matrix). When false, the
   * user cannot edit, delete, or back-date OTHER users' comments — moderation
   * is denied — but they can still create comments and manage their own.
   * Defaults to true.
   */
  allowCommentModeration?: boolean;
  interactiveWorkItemIds?: string[];
  /** RBAC feature settings for comment moderation checks (operations_job_edit_work_item_comments) */
  operationsFeatureSettings?: Record<string, unknown> | null;
  /** When true, also fetch comments from child appointments (used on Job view) */
  includeRelated?: boolean;
  enableSpeechToText?: boolean;
  restrictToClosingDetails?: boolean;
  appointmentWorkItem?: AppointmentTypeAwareWorkItem | null;
  appointmentTypeOptions?: ConfigurableTypeOption[];
  onAppointmentRequirementOverrideChange?: (
    requirementKey: AppointmentCompletionRequirementKey,
    updates: { enabled?: boolean; reason?: string }
  ) => void;
  /**
   * Team id for @-mention member list: client's Field Services (`field-services`) solution team
   * (Team Management RBAC), not the work item assignee team.
   */
  mentionTeamId?: string | null;
  /** When false, @-mention autocomplete is off (tokens in text can still be sent if typed/pasted). Default true. */
  enableMentions?: boolean;
  /**
   * `floating`: pen icon opens the composer in a modal (recommended for Billing sub-panels).
   * `inline`: classic embedded composer (used e.g. in Smart Task where nested scroll is tuned for it).
   */
  composerPresentation?: 'inline' | 'floating';
  /**
   * When `composerPresentation` is `floating`, controls the trigger button style.
   * `icon`: small square pen icon button (default, compact).
   * `full-button`: full-width dashed "+ New Comment" button (matches Smart Task tab pattern).
   */
  composerTriggerVariant?: 'icon' | 'full-button';
  /**
   * When true, suppress the success toast on comment save (e.g. in Smart Task
   * where the toast would overlay the Smart Action buttons). Errors still show.
   */
  suppressSuccessToast?: boolean;
}

interface CommentsResponse {
  success: boolean;
  data?: Comment[];
  total?: number;
  mention_team_id?: string | null;
  read_only?: boolean;
  read_only_reason?: string | null;
  can_moderate_comments?: boolean;
  can_toggle_comment_visibility?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string => {
  return authService.getTokens()?.access_token || '';
};

const COMMENT_OVERLAY_Z_CLASS = 'z-[200]';
const COMMENT_MENU_Z_CLASS = 'z-[205]';

const parseJsonResponse = async <T,>(response: Response): Promise<T | null> => {
  const responseText = await response.text().catch(() => '');
  const trimmedResponseText = responseText.trim();

  if (!trimmedResponseText) {
    return null;
  }

  try {
    return JSON.parse(trimmedResponseText) as T;
  } catch {
    return null;
  }
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const formatCommentDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDateTimeLocalValue = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = getDateStrSAST(date);
  const timeParts = getTimePartsSAST(date);
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${dateStr}T${pad(timeParts.hours)}:${pad(timeParts.minutes)}`;
};

const toIsoDateTime = (value: string): string | null => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const flattenComments = (items: Comment[]): Comment[] => {
  return items.flatMap((item) => [
    item,
    ...(item.replies ? flattenComments(item.replies) : []),
  ]);
};

const mergeCommentsById = (items: Comment[]): Comment[] => {
  const merged = new Map<string, Comment>();

  items.forEach((item) => {
    if (!merged.has(item.id)) {
      merged.set(item.id, item);
    }
  });

  return Array.from(merged.values());
};

/** Max time to wait on offline queue (IndexedDB); after this we continue with [] so the network fetch is never blocked indefinitely. */
const PENDING_COMMENTS_BUDGET_MS = 5000;

async function raceWithTimeBudget<T>(loader: () => Promise<T>, budgetMs: number, fallback: T): Promise<T> {
  if (typeof window === 'undefined') {
    try {
      return await loader();
    } catch {
      return fallback;
    }
  }
  try {
    return await Promise.race([
      loader(),
      new Promise<T>((resolve) => {
        window.setTimeout(() => resolve(fallback), budgetMs);
      }),
    ]);
  } catch {
    return fallback;
  }
}

/** Map POST/PUT JSON `data` into a Comment for optimistic UI; returns null if unusable. */
const normalizeCommentFromSaveResponse = (raw: unknown, fallbackWorkItemId: string): Comment | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id) {
    return null;
  }

  const work_item_id =
    typeof o.work_item_id === 'string' && o.work_item_id.trim().length > 0
      ? o.work_item_id
      : fallbackWorkItemId;

  const clientRaw = o.client_id;
  const client_id =
    typeof clientRaw === 'number' && Number.isFinite(clientRaw)
      ? clientRaw
      : typeof clientRaw === 'string'
        ? Number(clientRaw) || 0
        : 0;

  const userRaw = o.user_id;
  const user_id =
    typeof userRaw === 'number' && Number.isFinite(userRaw)
      ? userRaw
      : typeof userRaw === 'string'
        ? Number(userRaw) || 0
        : 0;

  const created_at =
    typeof o.created_at === 'string' ? o.created_at : new Date().toISOString();
  const updated_at =
    typeof o.updated_at === 'string' ? o.updated_at : created_at;

  const is_internal = Boolean(o.is_internal);
  const comment_type = getWorkItemCommentTypeForPersistence(
    typeof o.comment_type === 'string' ? o.comment_type : null,
    is_internal,
  );

  const mu = o.mentioned_users;
  const mentioned_users = Array.isArray(mu)
    ? mu.filter((x): x is number => typeof x === 'number')
    : undefined;

  const parentRaw = o.parent_comment_id;
  const parent_comment_id =
    typeof parentRaw === 'string' && parentRaw.length > 0 ? parentRaw : undefined;

  const is_edited = typeof o.is_edited === 'boolean' ? o.is_edited : undefined;
  const is_itemized = typeof o.is_itemized === 'boolean' ? o.is_itemized : undefined;

  return {
    id: o.id,
    work_item_id,
    client_id,
    user_id,
    user_name: typeof o.user_name === 'string' ? o.user_name : 'Unknown User',
    user_email: typeof o.user_email === 'string' ? o.user_email : undefined,
    comment_text: typeof o.comment_text === 'string' ? o.comment_text : '',
    is_internal,
    comment_type,
    mentioned_users,
    parent_comment_id,
    created_at,
    updated_at,
    deleted_at: typeof o.deleted_at === 'string' ? o.deleted_at : undefined,
    user_display_name:
      typeof o.user_display_name === 'string'
        ? o.user_display_name
        : typeof o.user_name === 'string'
          ? o.user_name
          : 'Unknown User',
    replies: [],
    source_appointment_id:
      o.source_appointment_id === null || o.source_appointment_id === undefined
        ? null
        : typeof o.source_appointment_id === 'string'
          ? o.source_appointment_id
          : null,
    source_appointment_number:
      typeof o.source_appointment_number === 'string' ? o.source_appointment_number : undefined,
    source_reference_kind:
      o.source_reference_kind === 'appointment' || o.source_reference_kind === 'group'
        ? o.source_reference_kind
        : null,
    offline_status: typeof o.offline_status === 'string' && (o.offline_status === 'pending' || o.offline_status === 'failed') ? o.offline_status : undefined,
    offline_error: typeof o.offline_error === 'string' ? o.offline_error : undefined,
    offline_action_id: typeof o.offline_action_id === 'string' ? o.offline_action_id : undefined,
    is_edited,
    is_itemized,
  };
};

const isNetworkAvailabilityError = (error: unknown): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  return error.name === 'TypeError'
    || /failed to fetch|network|offline|load failed/i.test(error.message);
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface CommentItemProps {
  comment: Comment;
  currentUserId: number | null;
  onEdit: (comment: Comment) => void;
  onDelete: (comment: Comment) => void;
  onToggleVisibility: (comment: Comment) => void;
  onChangeType: (comment: Comment, nextType: WorkItemCommentType) => void;
  isDark: boolean;
  canModerateComments: boolean;
  canToggleVisibility: boolean;
  enableExternalCommentType: boolean;
  closingDetailsLocked: boolean;
  readOnly?: boolean;
  isInteractive?: boolean;
  changingVisibilityCommentId?: string | null;
}

function CommentItem({
  comment,
  currentUserId,
  onEdit,
  onDelete,
  onToggleVisibility,
  onChangeType,
  isDark,
  canModerateComments,
  canToggleVisibility,
  enableExternalCommentType,
  closingDetailsLocked,
  readOnly = false,
  isInteractive = true,
  changingVisibilityCommentId = null,
}: CommentItemProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const commentType = resolveWorkItemCommentType(comment);
  const isOfflineQueuedComment = Boolean(comment.offline_status);
  const isOwner = currentUserId && comment.user_id === currentUserId;
  const isClosingDetailsLockedComment = enableExternalCommentType && closingDetailsLocked && commentType === 'closing_details';
  const canEditOrDeleteComment = !isOfflineQueuedComment && !readOnly && !isClosingDetailsLockedComment && (canModerateComments || (isInteractive && isOwner));
  const canChangeVisibility = !isOfflineQueuedComment && !readOnly && !isClosingDetailsLockedComment && (canModerateComments || canToggleVisibility || (isInteractive && isOwner));
  const canOpenMenu = canEditOrDeleteComment || canChangeVisibility;
  const isChangingVisibility = changingVisibilityCommentId === comment.id;

  const openSourceAppointment = useCallback(() => {
    if (!comment.source_appointment_id || typeof window === 'undefined') return;

    window.dispatchEvent(
      new CustomEvent('openWorkItem', {
        detail: { id: comment.source_appointment_id }
      })
    );
  }, [comment.source_appointment_id]);

  const handleMenuToggle = useCallback(() => {
    if (!showMenu && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top + window.scrollY,
        left: rect.right + window.scrollX - 224,
      });
    }
    setShowMenu(!showMenu);
  }, [showMenu]);

  const sourceBadgeKind: CommentSourceReferenceKind = comment.source_reference_kind === 'group'
    ? 'group'
    : 'appointment';
  const sourceBadgeMeta = getCommentSourceBadgeMeta(sourceBadgeKind, isDark);
  const commentTypeBadge = commentType === 'internal'
    ? {
      className: isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
      icon: <Lock size={10} />,
      label: 'Internal',
    }
    : commentType === 'external'
      ? {
        className: isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
        icon: <ExternalLink size={10} />,
        label: getWorkItemCommentTypeLabel('external'),
      }
      : {
        className: isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700',
        icon: <Globe size={10} />,
        label: enableExternalCommentType ? 'Closing Details' : 'Visible to Customer',
      };
  const availableCommentTypes = enableExternalCommentType
    ? (['internal', 'closing_details', 'external'] as WorkItemCommentType[]).filter(
      (nextType) => nextType !== commentType && !(closingDetailsLocked && nextType === 'closing_details')
    )
    : [];

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuButtonRef.current && !menuButtonRef.current.contains(e.target as Node) && !menuRef.current?.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative z-10">
      <div className="py-3">
        <div className="flex gap-3">
          <UserAvatar
            user={{
              userId: comment.user_id,
              displayName: comment.user_display_name || comment.user_name,
              email: comment.user_email,
            }}
            size="md"
          />

          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 flex-wrap">
              <UserIdentityLink
                user={{
                  userId: comment.user_id,
                  displayName: comment.user_display_name || comment.user_name,
                  email: comment.user_email,
                }}
                isDark={isDark}
                showAvatar={false}
                showExternalIcon
              />

              <span className={`px-1.5 py-0.5 text-xs rounded flex items-center gap-1 ${commentTypeBadge.className}`}>
                {commentTypeBadge.icon}
                {commentTypeBadge.label}
              </span>

              {isClosingDetailsLockedComment && (
                <span className={`px-1.5 py-0.5 text-[10px] rounded ${isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                  }`}>
                  Locked after signature
                </span>
              )}

              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {formatCommentDateTime(comment.created_at)}
              </span>

              {comment.offline_status === 'pending' && (
                <span className={`px-1.5 py-0.5 text-[10px] rounded ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'
                  }`}>
                  Offline pending
                </span>
              )}

              {comment.offline_status === 'failed' && (
                <span
                  className={`px-1.5 py-0.5 text-[10px] rounded ${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                    }`}
                  title={comment.offline_error || 'This comment will retry when Smart Task sync runs again.'}
                >
                  Sync retry pending
                </span>
              )}

              {comment.source_appointment_number && (
                comment.source_appointment_id ? (
                  <button
                    type="button"
                    onClick={openSourceAppointment}
                    className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full inline-flex items-center gap-1 transition-colors hover:opacity-90 ${sourceBadgeMeta.className}`}
                    title={`Open ${sourceBadgeMeta.titleLabel} ${comment.source_appointment_number}`}
                  >
                    <span className="rounded-full bg-black/10 px-1 py-0.5 text-[9px] font-bold leading-none dark:bg-white/10">
                      {sourceBadgeMeta.shortLabel}
                    </span>
                    <span>{comment.source_appointment_number}</span>
                    <ExternalLink size={10} />
                  </button>
                ) : (
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full inline-flex items-center gap-1 ${sourceBadgeMeta.className}`}>
                    <span className="rounded-full bg-black/10 px-1 py-0.5 text-[9px] font-bold leading-none dark:bg-white/10">
                      {sourceBadgeMeta.shortLabel}
                    </span>
                    {comment.source_appointment_number}
                  </span>
                )
              )}

              {new Date(comment.updated_at).getTime() !== new Date(comment.created_at).getTime() && (
                <span className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  (edited {formatCommentDateTime(comment.updated_at)})
                </span>
              )}
            </div>

            {/* Comment text */}
            <FormattedCommentBody
              text={comment.comment_text}
              isItemized={comment.is_itemized}
              className="mt-1 text-sm break-words"
              paragraphClassName={isDark ? 'text-gray-300' : 'text-gray-700'}
              listClassName={isDark ? 'text-gray-300' : 'text-gray-700'}
              listItemClassName={isDark ? 'text-gray-300' : 'text-gray-700'}
            />

            {/* Actions */}
            <div className="flex items-center justify-end mt-2">
              {/* Actions menu for owner/admin */}
              {canOpenMenu && (
                <div className="relative">
                  <button
                    ref={menuButtonRef}
                    onClick={handleMenuToggle}
                    aria-label="Comment actions"
                    className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                  >
                    <MoreHorizontal size={14} />
                  </button>

                  {showMenu && typeof document !== 'undefined' && createPortal(
                    <div
                      ref={menuRef}
                      className={`fixed w-56 rounded-lg shadow-lg border ${COMMENT_MENU_Z_CLASS} ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}
                      style={{ top: menuPosition.top, left: menuPosition.left, transform: 'translateY(-100%)', marginTop: '-4px' }}
                    >
                      {canEditOrDeleteComment && (
                        <button
                          onClick={() => {
                            onEdit(comment);
                            setShowMenu(false);
                          }}
                          className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 ${isDark
                            ? 'hover:bg-gray-700 text-gray-200'
                            : 'hover:bg-gray-50 text-gray-700'
                            }`}
                        >
                          <Edit3 size={14} />
                          Edit
                        </button>
                      )}
                      {canChangeVisibility && enableExternalCommentType && availableCommentTypes.map((nextType) => {
                        const nextTypeLabel = getWorkItemCommentTypeLabel(nextType);
                        const nextTypeIcon = nextType === 'internal'
                          ? <Lock size={14} />
                          : nextType === 'external'
                            ? <ExternalLink size={14} />
                            : <Globe size={14} />;

                        return (
                          <button
                            key={nextType}
                            onClick={() => {
                              onChangeType(comment, nextType);
                              setShowMenu(false);
                            }}
                            disabled={isChangingVisibility}
                            className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 ${isDark
                              ? 'hover:bg-gray-700 text-gray-200'
                              : 'hover:bg-gray-50 text-gray-700'
                              } disabled:opacity-60 disabled:cursor-not-allowed`}
                          >
                            {isChangingVisibility ? <Loader2 size={14} className="animate-spin" /> : nextTypeIcon}
                            {isChangingVisibility ? 'Changing status…' : `Change to ${nextTypeLabel.toLowerCase()}`}
                          </button>
                        );
                      })}
                      {canChangeVisibility && !enableExternalCommentType && (
                        <button
                          onClick={() => {
                            onToggleVisibility(comment);
                            setShowMenu(false);
                          }}
                          disabled={isChangingVisibility}
                          className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 ${isDark
                            ? 'hover:bg-gray-700 text-gray-200'
                            : 'hover:bg-gray-50 text-gray-700'
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          {isChangingVisibility ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : comment.is_internal ? (
                            <Globe size={14} />
                          ) : (
                            <Lock size={14} />
                          )}
                          {isChangingVisibility
                            ? 'Changing status…'
                            : comment.is_internal
                              ? 'Change to visible to customer'
                              : 'Change to internal'}
                        </button>
                      )}
                      {canEditOrDeleteComment && (
                        <button
                          onClick={() => {
                            onDelete(comment);
                            setShowMenu(false);
                          }}
                          className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 text-red-500 ${isDark
                            ? 'hover:bg-gray-700'
                            : 'hover:bg-gray-50'
                            }`}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}
                    </div>,
                    document.body
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CommentsSection({
  workItemId,
  secondaryWorkItemId,
  isActive,
  eagerLoad = false,
  onCommentsChange,
  onPublicCommentsChange,
  activityMetadata = null,
  showInternalToggle = true,
  defaultInternal = true,
  enableExternalCommentType = false,
  closingDetailsLocked = false,
  closingDetailsLockedReason,
  readOnly = false,
  allowCreateWhenReadOnly = false,
  readOnlyReason,
  allowCommentModeration = true,
  interactiveWorkItemIds,
  includeRelated = false,
  enableSpeechToText = true,
  restrictToClosingDetails = false,
  appointmentWorkItem,
  appointmentTypeOptions,
  onAppointmentRequirementOverrideChange,
  mentionTeamId: mentionTeamIdProp,
  enableMentions = true,
  composerPresentation = 'floating',
  composerTriggerVariant = 'icon',
  suppressSuccessToast = false,
  operationsFeatureSettings = null,
}: CommentsSectionProps) {
  const { user, isRoot } = useAuth();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  const getInitialCommentType = useCallback((): WorkItemCommentType | null => {
    if (restrictToClosingDetails) {
      return 'closing_details';
    }

    if (enableExternalCommentType) {
      if (defaultInternal) {
        return 'internal';
      }

      return closingDetailsLocked ? 'external' : 'closing_details';
    }

    if (defaultInternal) {
      return 'internal';
    }

    return 'closing_details';
  }, [closingDetailsLocked, defaultInternal, enableExternalCommentType, restrictToClosingDetails]);

  // State
  const [comments, setComments] = useState<Comment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState(() => createItemizedCommentDraft());
  const [commentType, setCommentType] = useState<WorkItemCommentType | null>(() => getInitialCommentType());
  const [isItemized, setIsItemized] = useState(true);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [commentCreatedAtInput, setCommentCreatedAtInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeechStarting, setIsSpeechStarting] = useState(false);
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false);
  const [audioRecordingDuration, setAudioRecordingDuration] = useState(0);
  const [speechRecognitionSupported, setSpeechRecognitionSupported] = useState(false);
  const [apiReadOnly, setApiReadOnly] = useState(false);
  const [apiReadOnlyReason, setApiReadOnlyReason] = useState<string | null>(null);
  const [apiCanModerateComments, setApiCanModerateComments] = useState(false);
  const [apiCanToggleCommentVisibility, setApiCanToggleCommentVisibility] = useState(false);
  const [updatingVisibilityCommentId, setUpdatingVisibilityCommentId] = useState<string | null>(null);
  const [mentionTeamIdFromCommentsApi, setMentionTeamIdFromCommentsApi] = useState<string | null>(null);
  const [mentionCandidates, setMentionCandidates] = useState<Array<{ user_id: number; label: string }>>([]);
  const [mentionMembersLoading, setMentionMembersLoading] = useState(false);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionHighlight, setMentionHighlight] = useState(0);
  const [composerModalOpen, setComposerModalOpen] = useState(false);

  // Refs to guard against unnecessary callback invocations that cause infinite loops
  const prevCommentCountRef = useRef<number | null>(null);
  const prevPublicCommentsKeyRef = useRef<string | null>(null);

  // The floating composer modal uses `position: fixed; inset: 0` which automatically
  // respects the visual viewport (shrinks above the keyboard) on Android WebView
  // with adjustResize — no manual viewport height tracking needed.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionRangeRef = useRef<{ start: number } | null>(null);
  const hasLoadedRef = useRef(false);
  const loadRequestIdRef = useRef(0);
  const speechRecognitionRef = useRef<SpeechToTextController | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioRecordingTimerRef = useRef<number | null>(null);
  const realtimeRefreshTimeoutRef = useRef<number | null>(null);
  const speechDraftBaseRef = useRef('');

  // Get current user ID
  const currentUserId = user?.id || null;
  const clientId = user?.client_id ? String(user.client_id) : null;
  const numericClientId = user?.client_id ? Number(user.client_id) : null;
  const currentUserDisplayName = getUserDisplayName({
    firstName: user?.first_name,
    lastName: user?.last_name,
    displayName: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
    email: user?.email,
  }) || user?.email || 'You';
  const interactiveWorkItemIdSet = React.useMemo(() => {
    const ids = new Set<string>();
    ids.add(String(workItemId));
    if (interactiveWorkItemIds?.length) {
      interactiveWorkItemIds.forEach((id) => ids.add(String(id)));
    }
    // Include work_item_ids from loaded comments so child appointment comments
    // are interactive when viewed from a parent job page
    comments.forEach((comment) => {
      if (comment.work_item_id) {
        ids.add(String(comment.work_item_id));
      }
    });
    return ids;
  }, [interactiveWorkItemIds, workItemId, comments]);

  const resolvedMentionTeamId = useMemo(() => {
    if (!enableMentions) {
      return null;
    }
    const trimmedProp =
      typeof mentionTeamIdProp === 'string' && mentionTeamIdProp.trim().length > 0
        ? mentionTeamIdProp.trim()
        : null;
    return trimmedProp ?? mentionTeamIdFromCommentsApi;
  }, [enableMentions, mentionTeamIdProp, mentionTeamIdFromCommentsApi]);

  const canModerateComments = allowCommentModeration && (apiCanModerateComments || hasPrivilegedCommentRole(user) || isRoot);
  const canToggleCommentVisibility = canModerateComments || apiCanToggleCommentVisibility;
  const canCreateComments = (!readOnly || allowCreateWhenReadOnly) && !apiReadOnly;
  const canManageExistingComments = !readOnly && !apiReadOnly;
  const commentsReadOnly = !canCreateComments;
  const createOnlyMode = canCreateComments && !canManageExistingComments;
  const resolvedReadOnlyReason = readOnlyReason || apiReadOnlyReason || 'Comments are read-only because this appointment is in a terminal state.';
  const resolvedCreateOnlyReason = readOnlyReason || 'You can add new comments, but existing comments remain read-only until this appointment becomes editable.';
  const selectedCommentTypeLabel = commentType ? getWorkItemCommentTypeLabel(commentType) : 'Comment';
  const isInternal = commentType === 'internal';
  const visibleComments = React.useMemo(
    () => restrictToClosingDetails
      ? comments.filter((comment) => isClosingDetailsComment(comment) && !comment.deleted_at && !isBillingWorkItemComment(comment))
      : comments.filter((comment) => !isBillingWorkItemComment(comment)),
    [comments, restrictToClosingDetails]
  );
  const shouldShowCommentTypeSelector = (enableExternalCommentType || showInternalToggle) && !restrictToClosingDetails;
  const closingDetailsComposerLocked = enableExternalCommentType && closingDetailsLocked && commentType === 'closing_details';
  const resolvedClosingDetailsLockedReason = closingDetailsLockedReason || 'Closing details are read-only after the customer has signed this appointment.';
  const hasSelectedCommentType = Boolean(commentType);
  const commentPlaceholder = !commentType
    ? 'Choose Closing, External, or Internal above to start typing...'
    : commentType === 'internal'
      ? isItemized
        ? '1. Add the first internal comment...'
        : 'Add an internal comment...'
      : commentType === 'external'
        ? isItemized
          ? '1. Add the first external comment...'
          : 'Add an external comment...'
        : isItemized
          ? '1. Describe the first closing detail...'
          : 'Describe the closing detail...';
  /** Capacitor/WebKit: nested overflow-y regions steal touch scrolling — user never reaches the composer below the list. Portal keeps the capped inner scroll. */
  const isNativeApp = isCapacitor();
  const useCompactComposerLayout = isNativeApp;
  const commentTypeOptions = React.useMemo(() => ([
    {
      type: 'closing_details' as WorkItemCommentType,
      label: getWorkItemCommentTypeLabel('closing_details'),
      compactLabel: 'Closing',
      icon: Globe,
    },
    {
      type: 'external' as WorkItemCommentType,
      label: 'Customer',
      compactLabel: 'Customer',
      icon: ExternalLink,
    },
    {
      type: 'internal' as WorkItemCommentType,
      label: getWorkItemCommentTypeLabel('internal'),
      compactLabel: 'Internal',
      icon: Lock,
    },
  ]), []);

  useEffect(() => {
    setMentionTeamIdFromCommentsApi(null);
    setMentionCandidates([]);
    setMentionMenuOpen(false);
    setComposerModalOpen(false);
  }, [workItemId]);

  useEffect(() => {
    if (!enableMentions) {
      setMentionMenuOpen(false);
    }
  }, [enableMentions]);

  useEffect(() => {
    if (!resolvedMentionTeamId) {
      setMentionCandidates([]);
      setMentionMembersLoading(false);
      return;
    }

    let cancelled = false;
    setMentionMembersLoading(true);

    void (async () => {
      try {
        const response = await fetch(`/api/v1/teams/${encodeURIComponent(resolvedMentionTeamId)}/members`, {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        });
        const data = await parseJsonResponse<{
          success?: boolean;
          data?: Array<{
            user_id?: number;
            user?: { first_name?: string | null; last_name?: string | null; email?: string | null };
          }>;
        }>(response);

        if (cancelled) {
          return;
        }

        if (!response.ok || !data?.success || !Array.isArray(data.data)) {
          setMentionCandidates([]);
          return;
        }

        const list: Array<{ user_id: number; label: string }> = [];
        for (const row of data.data) {
          const uid = Number(row.user_id);
          if (!Number.isFinite(uid)) {
            continue;
          }
          const u = row.user || {};
          const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
            || (typeof u.email === 'string' ? u.email : '')
            || `User ${uid}`;
          list.push({ user_id: uid, label: name });
        }
        setMentionCandidates(list);
      } catch (err) {
        if (!isSilentFetchError(err)) console.warn('[CommentsSection] Failed to load team members for mentions:', err);
        if (!cancelled) {
          setMentionCandidates([]);
        }
      } finally {
        if (!cancelled) {
          setMentionMembersLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedMentionTeamId]);

  const filteredMentionSuggestions = useMemo(() => {
    const selfId = currentUserId != null ? Number(currentUserId) : null;
    const q = mentionFilter.trim().toLowerCase();
    return mentionCandidates
      .filter((m) => (selfId == null || !Number.isFinite(selfId) ? true : m.user_id !== selfId))
      .filter((m) => {
        if (!q) {
          return true;
        }
        return m.label.toLowerCase().includes(q);
      });
  }, [mentionCandidates, mentionFilter, currentUserId]);

  useEffect(() => {
    setMentionHighlight((h) => {
      if (filteredMentionSuggestions.length === 0) {
        return 0;
      }
      return Math.min(h, filteredMentionSuggestions.length - 1);
    });
  }, [filteredMentionSuggestions.length]);

  useEffect(() => {
    let cancelled = false;

    if (!enableSpeechToText) {
      setSpeechRecognitionSupported(false);
      return () => {
        cancelled = true;
      };
    }

    void isSpeechToTextSupported().then((supported) => {
      if (!cancelled) {
        setSpeechRecognitionSupported(supported);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enableSpeechToText]);

  const mergeTranscriptIntoDraft = useCallback((baseText: string, transcript: string) => {
    const spokenText = transcript.trim();
    if (!spokenText) {
      return baseText;
    }

    return isItemized
      ? mergeSpeechIntoItemizedComment(baseText, spokenText)
      : mergeSpeechIntoPlainComment(baseText, spokenText);
  }, [isItemized]);

  const mergeRecordedTranscriptIntoDraft = useCallback((baseText: string, transcript: string) => {
    const spokenText = transcript.trim();
    if (!spokenText) {
      return baseText;
    }

    return isItemized
      ? appendRecordedSpeechToItemizedComment(baseText, spokenText)
      : appendRecordedSpeechToPlainComment(baseText, spokenText);
  }, [isItemized]);

  const clearAudioRecordingTimer = useCallback(() => {
    if (audioRecordingTimerRef.current !== null) {
      window.clearInterval(audioRecordingTimerRef.current);
      audioRecordingTimerRef.current = null;
    }
  }, []);

  const stopAudioStream = useCallback(() => {
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioStreamRef.current = null;
  }, []);

  const transcribeRecordedAudio = useCallback(async (audioBlob: Blob) => {
    const extension = audioBlob.type.includes('ogg') ? 'ogg' : audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
    const file = new File([audioBlob], `comment-${Date.now()}.${extension}`, {
      type: audioBlob.type || 'audio/webm',
    });

    const formData = new FormData();
    formData.append('audio', file);
    formData.append('prompt', 'Transcribe this field service job closing comment accurately. Preserve details, numbering, and sentence order.');

    const response = await fetch('/api/v1/ai/transcribe-audio', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: formData,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to transcribe audio');
    }

    return typeof data?.data?.text === 'string' ? data.data.text.trim() : '';
  }, []);

  const stopSpeechToText = useCallback(async (keepFocus = true, discardAudio = false) => {
    const activeController = speechRecognitionRef.current;
    speechRecognitionRef.current = null;

    const activeRecorder = mediaRecorderRef.current;
    if (activeRecorder) {
      const stream = audioStreamRef.current;

      if (discardAudio) {
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        clearAudioRecordingTimer();
        setIsAudioRecording(false);
        setIsTranscribingAudio(false);
        setAudioRecordingDuration(0);

        try {
          activeRecorder.ondataavailable = null;
          activeRecorder.onstop = null;
          activeRecorder.onerror = null;
          if (activeRecorder.state !== 'inactive') {
            activeRecorder.stop();
          }
        } catch {
          // Ignore teardown failures when abandoning an in-flight recording.
        }

        stream?.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      } else if (activeRecorder.state !== 'inactive') {
        setIsSpeechStarting(false);
        activeRecorder.stop();
      }

      if (keepFocus && discardAudio) {
        textareaRef.current?.focus();
      }

      return;
    }

    if (activeController) {
      try {
        await activeController.stop();
      } catch {
        // Ignore browsers/platforms that fail while stopping an inactive recognizer.
      } finally {
        await activeController.destroy();
      }
    }

    setIsListening(false);
    setIsSpeechStarting(false);

    if (keepFocus) {
      textareaRef.current?.focus();
    }
  }, [clearAudioRecordingTimer]);

  const startRecordedSpeechCapture = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      throw new Error('Audio recording is not supported on this device');
    }

    setIsSpeechStarting(true);

    if (speechRecognitionRef.current) {
      await speechRecognitionRef.current.destroy();
      speechRecognitionRef.current = null;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const supportedMimeType = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ].find((mimeType) => {
      try {
        return typeof MediaRecorder.isTypeSupported === 'function' ? MediaRecorder.isTypeSupported(mimeType) : false;
      } catch {
        return false;
      }
    });

    const mediaRecorder = supportedMimeType
      ? new MediaRecorder(stream, { mimeType: supportedMimeType })
      : new MediaRecorder(stream);

    audioStreamRef.current = stream;
    audioChunksRef.current = [];
    mediaRecorderRef.current = mediaRecorder;
    speechDraftBaseRef.current = newComment;
    setAudioRecordingDuration(0);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onerror = () => {
      console.warn('[CommentsSection] Audio recording error', { workItemId });
      stopAudioStream();
      clearAudioRecordingTimer();
      mediaRecorderRef.current = null;
      setIsAudioRecording(false);
      setIsSpeechStarting(false);
      setAudioRecordingDuration(0);
      notify.error('Audio recording failed before transcription could start');
    };

    mediaRecorder.onstop = async () => {
      const recordedChunks = [...audioChunksRef.current];
      const mimeType = mediaRecorder.mimeType || 'audio/webm';

      audioChunksRef.current = [];
      mediaRecorderRef.current = null;
      clearAudioRecordingTimer();
      stopAudioStream();
      setIsAudioRecording(false);
      setAudioRecordingDuration(0);

      if (!recordedChunks.length) {
        setIsTranscribingAudio(false);
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
        return;
      }

      const audioBlob = new Blob(recordedChunks, { type: mimeType });
      if (audioBlob.size === 0) {
        setIsTranscribingAudio(false);
        notify.error('No audio was captured for transcription');
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
        return;
      }

      setIsTranscribingAudio(true);

      try {
        const transcript = await transcribeRecordedAudio(audioBlob);

        if (!transcript) {
          notify.error('No speech was detected in the recording');
          return;
        }

        setNewComment(mergeRecordedTranscriptIntoDraft(speechDraftBaseRef.current, transcript));
        notify.success('Comment transcribed');
      } catch (error: unknown) {
        console.error('[CommentsSection] Audio transcription failed:', error);
        notify.error(getErrorMessage(error, 'Audio transcription failed'));
      } finally {
        setIsTranscribingAudio(false);
        textareaRef.current?.focus();
      }
    };

    mediaRecorder.start();
    setIsAudioRecording(true);
    setIsSpeechStarting(false);

    audioRecordingTimerRef.current = window.setInterval(() => {
      setAudioRecordingDuration((prev) => prev + 1);
    }, 1000);
  }, [clearAudioRecordingTimer, mergeRecordedTranscriptIntoDraft, newComment, stopAudioStream, transcribeRecordedAudio, workItemId]);

  const startSpeechToText = useCallback(async () => {
    if (!canCreateComments) {
      return;
    }

    if (!enableSpeechToText) {
      return;
    }

    if (isSpeechStarting) {
      return;
    }

    if (isAudioRecording) {
      await stopSpeechToText();
      return;
    }

    if (isTranscribingAudio) {
      return;
    }

    if (isListening) {
      await stopSpeechToText();
      return;
    }

    const canUseRecordedTranscription = isCapacitor() && typeof navigator !== 'undefined' && typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

    if (canUseRecordedTranscription) {
      try {
        console.info('[CommentsSection] Starting recorded speech transcription', {
          workItemId,
        });
        await startRecordedSpeechCapture();
        return;
      } catch (error) {
        console.warn('[CommentsSection] Recorded transcription unavailable, falling back to live speech recognition', error);
        setIsSpeechStarting(false);
      }
    }

    setIsSpeechStarting(true);

    console.info('[CommentsSection] Speech-to-text requested', {
      workItemId,
      isCapacitor: isCapacitor(),
      supportedState: speechRecognitionSupported,
      composeMode: 'itemized',
    });

    const supported = speechRecognitionSupported || await isSpeechToTextSupported();
    setSpeechRecognitionSupported(supported);

    if (!supported && !isCapacitor()) {
      setIsSpeechStarting(false);
      notify.error('Speech-to-text is not supported in this browser');
      return;
    }

    if (speechRecognitionRef.current) {
      await speechRecognitionRef.current.destroy();
      speechRecognitionRef.current = null;
    }

    const buildController = (usePopup: boolean) => createSpeechToTextController({
      continuous: true,
      interimResults: true,
      androidPopup: usePopup,
      androidPrompt: usePopup ? 'Speak your comment' : undefined,
      onListeningChange: (nextListening) => {
        setIsListening(nextListening);
        if (nextListening) {
          setIsSpeechStarting(false);
          console.info('[CommentsSection] Speech-to-text listening', {
            workItemId,
            source: isCapacitor() ? 'capacitor' : 'web',
            mode: usePopup ? 'popup' : 'background',
          });
        }
      },
      onResult: (snapshot) => {
        setNewComment(mergeTranscriptIntoDraft(speechDraftBaseRef.current, snapshot.transcript));
      },
      onError: (error) => {
        console.warn('[CommentsSection] Speech recognition error', {
          workItemId,
          code: error.code,
          fatal: error.fatal,
          source: error.source,
          mode: usePopup ? 'popup' : 'background',
        });

        if (error.code === 'aborted' || error.code === 'no-speech' || error.code === 'no-match') {
          return;
        }

        console.warn('Speech recognition error:', error.code);

        if (error.fatal || error.code === 'client' || error.code === 'busy' || error.code === 'start-failed') {
          notify.error(error.message);
        }
      },
    });

    const tryStartController = async (usePopup: boolean): Promise<boolean> => {
      const controller = buildController(usePopup);
      speechRecognitionRef.current = controller;

      const started = await controller.start();
      if (started) {
        return true;
      }

      if (speechRecognitionRef.current === controller) {
        speechRecognitionRef.current = null;
      }

      await controller.destroy();
      return false;
    };

    speechDraftBaseRef.current = newComment;

    const prefersBackgroundMode = isCapacitor();
    const started = await tryStartController(false);

    if (!started && prefersBackgroundMode) {
      console.info('[CommentsSection] Retrying speech-to-text with Android popup fallback', {
        workItemId,
      });

      const popupStarted = await tryStartController(true);
      if (popupStarted) {
        setIsSpeechStarting(false);
        return;
      }
    }

    setIsSpeechStarting(false);

    if (!started) {
      console.warn('[CommentsSection] Speech-to-text failed to start', {
        workItemId,
        isCapacitor: isCapacitor(),
      });

      setIsListening(false);
      notify.error('Unable to start speech-to-text right now');
    }
  }, [canCreateComments, enableSpeechToText, isAudioRecording, isListening, isSpeechStarting, isTranscribingAudio, mergeTranscriptIntoDraft, newComment, speechRecognitionSupported, startRecordedSpeechCapture, stopSpeechToText, workItemId]);

  useEffect(() => {
    hasLoadedRef.current = false;
    setComments([]);
    setEditingComment(null);
    setIsItemized(true);
    setNewComment(createItemizedCommentDraft());
    setCommentType(getInitialCommentType());
    setCommentCreatedAtInput('');
    setApiReadOnly(false);
    setApiReadOnlyReason(null);
    setApiCanModerateComments(false);
    setApiCanToggleCommentVisibility(false);
    setUpdatingVisibilityCommentId(null);
    prevCommentCountRef.current = null;
    prevPublicCommentsKeyRef.current = null;
    void stopSpeechToText(false, true);
  }, [defaultInternal, getInitialCommentType, secondaryWorkItemId, stopSpeechToText, workItemId]);

  useEffect(() => {
    if (canManageExistingComments) {
      return;
    }

    setEditingComment(null);
    setCommentCreatedAtInput('');
    void stopSpeechToText(false, true);
  }, [canManageExistingComments, stopSpeechToText]);

  useEffect(() => {
    if (canCreateComments) {
      return;
    }

    setIsItemized(true);
    setNewComment(createItemizedCommentDraft());
    setCommentCreatedAtInput('');
    void stopSpeechToText(false, true);
  }, [canCreateComments, stopSpeechToText]);

  // Notify parent of comment count changes
  useEffect(() => {
    if (!restrictToClosingDetails) {
      return;
    }

    setCommentType('closing_details');
  }, [restrictToClosingDetails]);

  useEffect(() => {
    if (restrictToClosingDetails) {
      return;
    }

    if (!enableExternalCommentType || !closingDetailsLocked) {
      return;
    }

    setCommentType((currentType) => (currentType === 'closing_details' ? getInitialCommentType() : currentType));
  }, [closingDetailsLocked, enableExternalCommentType, getInitialCommentType, restrictToClosingDetails]);

  useEffect(() => {
    const allComments = flattenComments(comments);
    const allVisibleComments = flattenComments(visibleComments);

    const nextCount = restrictToClosingDetails ? allVisibleComments.length : allComments.length;
    if (onCommentsChange && prevCommentCountRef.current !== nextCount) {
      prevCommentCountRef.current = nextCount;
      onCommentsChange(nextCount);
    }

    if (onPublicCommentsChange) {
      const publicComments = allComments
        .filter((comment) => (isClosingDetailsComment(comment) || isExternalWorkItemComment(comment)) && !comment.deleted_at)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        .map((comment) => ({
          id: comment.id,
          user_name: comment.user_name,
          comment_text: comment.comment_text,
          created_at: comment.created_at,
          is_internal: comment.is_internal,
          comment_type: comment.comment_type,
          is_itemized: comment.is_itemized,
        }));

      const publicCommentsKey = publicComments.map((c) => `${c.id}:${c.comment_text}:${c.created_at}`).join('|');
      if (prevPublicCommentsKeyRef.current !== publicCommentsKey) {
        prevPublicCommentsKeyRef.current = publicCommentsKey;
        onPublicCommentsChange(publicComments);
      }
    }
  }, [comments, onCommentsChange, onPublicCommentsChange, restrictToClosingDetails, visibleComments]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [newComment]);

  useEffect(() => {
    return () => {
      void stopSpeechToText(false, true);
    };
  }, [stopSpeechToText]);

  // ============================================================================
  // API FUNCTIONS
  // ============================================================================

  const organizeNotes = (flatComments: Comment[]): Comment[] => {
    return [...flatComments]
      .map((comment) => ({ ...comment, replies: [] }))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  const mapQueuedCommentToComment = useCallback((entry: SmartTaskQueuedCommentCreate): Comment => ({
    id: `offline:${entry.offline_action_id}`,
    work_item_id: entry.work_item_id,
    client_id: entry.client_id,
    user_id: entry.user_id,
    user_name: currentUserDisplayName,
    user_email: user?.email,
    comment_text: entry.payload.comment_text,
    is_internal: entry.comment_type === 'internal',
    comment_type: entry.comment_type,
    mentioned_users: entry.payload.mentioned_user_ids,
    parent_comment_id: entry.payload.parent_comment_id || undefined,
    created_at: entry.created_at,
    updated_at: entry.updated_at,
    user_display_name: currentUserDisplayName,
    replies: [],
    offline_status: entry.last_error ? 'failed' : 'pending',
    offline_error: entry.last_error || null,
    offline_action_id: entry.offline_action_id,
    is_itemized: entry.payload.is_itemized,
  }), [currentUserDisplayName, user?.email]);

  const loadPendingComments = useCallback(async (): Promise<Comment[]> => {
    if (!numericClientId || !currentUserId) {
      return [];
    }

    const scopedWorkItemIds = Array.from(new Set(
      [workItemId, secondaryWorkItemId]
        .filter((value): value is string => Boolean(value))
        .map(String)
    ));

    if (scopedWorkItemIds.length === 0) {
      return [];
    }

    const pendingComments = await masterSync.getPendingSmartTaskComments({
      clientId: numericClientId,
      userId: Number(currentUserId),
      workItemIds: scopedWorkItemIds,
    });

    return pendingComments.map(mapQueuedCommentToComment);
  }, [currentUserId, mapQueuedCommentToComment, numericClientId, secondaryWorkItemId, workItemId]);

  const loadComments = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    const mergeAndOrganizeComments = (baseComments: Comment[], extraComments: Comment[] = []) => {
      return organizeNotes(mergeCommentsById([...baseComments, ...extraComments]));
    };

    try {
      const relatedParam = includeRelated ? '?include_related=true' : '';

      // Offline-first: show queued Smart Task comments as soon as IndexedDB responds (short budget) so the tab is never blank while the store wakes up.
      const fastOffline = await raceWithTimeBudget(
        () => loadPendingComments().catch((error) => {
          console.warn('[CommentsSection] Failed to load pending offline comments:', error);
          return [] as Comment[];
        }),
        450,
        [] as Comment[],
      );
      if (fastOffline.length > 0) {
        setComments((prev) => (flattenComments(prev).length > 0
          ? prev
          : organizeNotes(mergeCommentsById(fastOffline))));
      }

      const loadPendingSafe = () => loadPendingComments().catch((error) => {
        console.warn('[CommentsSection] Failed to load pending offline comments:', error);
        return [] as Comment[];
      });

      const [pendingComments, response] = await Promise.all([
        raceWithTimeBudget(loadPendingSafe, PENDING_COMMENTS_BUDGET_MS, [] as Comment[]),
        fetch(`/api/v1/work-items/${workItemId}/comments${relatedParam}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }),
      ]);

      if (loadRequestIdRef.current !== requestId) {
        return;
      }

      if (response.ok) {
        const data: CommentsResponse = await response.json();
        if (data.success) {
          setApiReadOnly(Boolean(data.read_only));
          setApiReadOnlyReason(typeof data.read_only_reason === 'string' ? data.read_only_reason : null);
          setApiCanModerateComments(Boolean(data.can_moderate_comments));
          setApiCanToggleCommentVisibility(Boolean(data.can_toggle_comment_visibility));

          const nextMentionTeam =
            typeof data.mention_team_id === 'string' && data.mention_team_id.trim().length > 0
              ? data.mention_team_id.trim()
              : null;
          setMentionTeamIdFromCommentsApi(nextMentionTeam);

          const primaryComments = Array.isArray(data.data) ? data.data : [];
          setComments(mergeAndOrganizeComments(primaryComments, pendingComments));

          if (secondaryWorkItemId && String(secondaryWorkItemId) !== String(workItemId)) {
            void (async () => {
              try {
                const secondaryResponse = await fetch(`/api/v1/work-items/${secondaryWorkItemId}/comments`, {
                  headers: {
                    'Authorization': `Bearer ${getAuthToken()}`
                  }
                });

                if (!secondaryResponse.ok || loadRequestIdRef.current !== requestId) {
                  return;
                }

                const secondaryData: CommentsResponse = await secondaryResponse.json();
                if (!secondaryData.success || !Array.isArray(secondaryData.data) || loadRequestIdRef.current !== requestId) {
                  return;
                }

                setComments((currentComments) => mergeAndOrganizeComments(
                  mergeCommentsById([...flattenComments(currentComments), ...pendingComments]),
                  secondaryData.data || []
                ));
              } catch (secondaryError) {
                if (!isSilentFetchError(secondaryError)) console.warn('[CommentsSection] Failed to load secondary comments:', secondaryError);
              }
            })();
          }

          return;
        }
      }

      setComments(organizeNotes(pendingComments));
    } catch (error) {
      console.error('Error loading comments:', error);

      if (loadRequestIdRef.current !== requestId) {
        return;
      }

      const pendingComments = await raceWithTimeBudget(
        () => loadPendingComments().catch(() => [] as Comment[]),
        PENDING_COMMENTS_BUDGET_MS,
        [] as Comment[],
      );
      setComments(organizeNotes(pendingComments));

      if (!isNetworkAvailabilityError(error) && pendingComments.length === 0) {
        notify.error('Failed to load comments');
      }
    }
  }, [includeRelated, loadPendingComments, secondaryWorkItemId, workItemId]);

  // When the external readOnly prop is lifted, reset apiReadOnly and re-fetch
  // to ensure the UI reflects the latest server-side comment permissions.
  const wasReadOnlyRef = React.useRef(readOnly);
  useEffect(() => {
    if (wasReadOnlyRef.current && !readOnly) {
      setApiReadOnly(false);
      setApiReadOnlyReason(null);
      void loadComments();
    }
    wasReadOnlyRef.current = readOnly;
  }, [readOnly, loadComments]);

  // Load comments when tab becomes active
  useEffect(() => {
    if ((isActive || eagerLoad) && !hasLoadedRef.current) {
      void loadComments();
      hasLoadedRef.current = true;
    }
  }, [eagerLoad, isActive, loadComments]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (!isActive || !hasLoadedRef.current) {
      return;
    }

    if (typeof window === 'undefined') {
      void loadComments();
      return;
    }

    if (realtimeRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
    }

    realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      realtimeRefreshTimeoutRef.current = null;
      void loadComments();
    }, 80);
  }, [isActive, loadComments]);

  useWebSocketChannel({
    channel: clientId ? `operations:client:${clientId}` : undefined,
    autoConnect: true,
    retainMessages: false,
    retainLatestData: false,
    onMessage: (data) => {
      const message = (data || {}) as Record<string, unknown>;
      const type = typeof message.type === 'string' ? message.type : '';
      if (
        type.startsWith('appointment_')
        || type.startsWith('work_item_')
        || type === 'notification'
        || type === 'operations_refresh'
      ) {
        scheduleRealtimeRefresh();
      }
    },
  });

  useEffect(() => {
    return () => {
      if (realtimeRefreshTimeoutRef.current && typeof window !== 'undefined') {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  const queueCommentOffline = useCallback(async (commentText: string) => {
    if (!numericClientId || !currentUserId) {
      throw new Error('No active client found for offline comments');
    }

    if (!commentType) {
      throw new Error('Select a comment type before saving');
    }

    const mentionIds = extractMentionUserIdsFromCommentText(commentText);

    const queuedEntry = await masterSync.queueSmartTaskComment({
      clientId: numericClientId,
      userId: Number(currentUserId),
      workItemId: String(workItemId),
      payload: {
        comment_text: commentText,
        is_internal: isInternal,
        comment_type: commentType,
        is_itemized: isItemized,
        activity_metadata: activityMetadata || undefined,
        ...(mentionIds.length > 0 ? { mentioned_user_ids: mentionIds } : {}),
      },
      timelineLabel: `Offline ${selectedCommentTypeLabel.toLowerCase()} comment`,
    });

    // Append the queued entry immediately — a plain reload can overwrite the
    // list with a stale cached response that does not include it yet, making
    // the comment look like it silently vanished.
    setComments((prev) => organizeNotes(
      mergeCommentsById([...flattenComments(prev), mapQueuedCommentToComment(queuedEntry)])
    ));

    setNewComment(isItemized ? createItemizedCommentDraft() : createPlainCommentDraft());
    setCommentType(getInitialCommentType());
    setEditingComment(null);
    setCommentCreatedAtInput('');
    void stopSpeechToText(false, true);
  }, [activityMetadata, commentType, currentUserId, getInitialCommentType, isInternal, isItemized, mapQueuedCommentToComment, numericClientId, selectedCommentTypeLabel, stopSpeechToText, workItemId]);

  const submitComment = async () => {
    if (!canCreateComments || !commentType) {
      if (canCreateComments && !commentType) {
        notify.error('Select a comment type before saving');
      }
      return;
    }

    const commentText = isItemized
      ? serializeItemizedCommentDraft(newComment)
      : serializePlainCommentDraft(newComment);
    const mentionIds = extractMentionUserIdsFromCommentText(commentText);

    if (!commentText || submitting) return;

    const editedCreatedAt = editingComment && canModerateComments
      ? toIsoDateTime(commentCreatedAtInput)
      : undefined;

    if (editingComment && canModerateComments && commentCreatedAtInput && !editedCreatedAt) {
      notify.error('Please enter a valid comment date and time');
      return;
    }

    const savedWasEditing = Boolean(editingComment);

    try {
      setSubmitting(true);

      const payload = {
        comment_text: commentText,
        is_internal: isInternal,
        comment_type: commentType,
        is_itemized: isItemized,
        created_at: editingComment && canModerateComments ? editedCreatedAt || undefined : undefined,
        activity_metadata: activityMetadata || undefined,
        ...(mentionIds.length > 0 ? { mentioned_user_ids: mentionIds } : {}),
      };
      const targetWorkItemId = editingComment?.work_item_id ? String(editingComment.work_item_id) : String(workItemId);

      const url = editingComment
        ? `/api/v1/work-items/${targetWorkItemId}/comments?comment_id=${editingComment.id}`
        : `/api/v1/work-items/${workItemId}/comments`;

      const response = await fetch(url, {
        method: editingComment ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await parseJsonResponse<{
        success?: boolean;
        error?: string;
        data?: unknown;
      }>(response);

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to save comment');
      }

      if (data?.success === false) {
        throw new Error(data.error || 'Failed to save comment');
      }

      const optimistic = normalizeCommentFromSaveResponse(data?.data, targetWorkItemId);

      setNewComment(isItemized ? createItemizedCommentDraft() : createPlainCommentDraft());
      setCommentType(getInitialCommentType());
      setEditingComment(null);
      setCommentCreatedAtInput('');

      if (optimistic) {
        setComments((prev) => {
          const flat = flattenComments(prev);
          if (savedWasEditing) {
            const without = flat.filter((c) => c.id !== optimistic.id);
            return organizeNotes(mergeCommentsById([...without, optimistic]));
          }
          return organizeNotes(mergeCommentsById([...flat, optimistic]));
        });
      }

      void loadComments();
      if (!suppressSuccessToast) {
        notify.success(savedWasEditing ? 'Details updated' : 'Details saved');
      }
      if (composerPresentation === 'floating') {
        setComposerModalOpen(false);
      }
    } catch (error: unknown) {
      console.error('Error saving comment:', error);

      if (!editingComment && isNetworkAvailabilityError(error)) {
        try {
          await queueCommentOffline(commentText);
          // Refresh so the queued entry renders via loadComments' pending
          // merge — without this the comment saves to the outbox but stays
          // invisible and looks like it was never saved.
          void loadComments();
          if (!suppressSuccessToast) {
            notify.info('You are offline. Comment saved locally and will sync when you reconnect.');
          }
          return;
        } catch (offlineQueueError: unknown) {
          console.error('Error queueing offline comment:', offlineQueueError);
          notify.error(getErrorMessage(offlineQueueError, 'Failed to save details offline'));
          return;
        }
      }

      notify.error(getErrorMessage(error, 'Failed to save details'));
    } finally {
      setSubmitting(false);
    }
  };

  const changeCommentType = async (comment: Comment, nextType: WorkItemCommentType) => {
    if (!canManageExistingComments || updatingVisibilityCommentId) {
      return;
    }

    if (enableExternalCommentType && closingDetailsLocked && nextType === 'closing_details') {
      notify.error(resolvedClosingDetailsLockedReason);
      return;
    }

    try {
      setUpdatingVisibilityCommentId(comment.id);
      const targetWorkItemId = comment.work_item_id ? String(comment.work_item_id) : String(workItemId);

      const response = await fetch(`/api/v1/work-items/${targetWorkItemId}/comments/${comment.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_internal: nextType === 'internal',
          comment_type: nextType,
        }),
      });

      const data = await parseJsonResponse<{ success?: boolean; error?: string }>(response);

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to change comment status');
      }

      if (data?.success === false) {
        throw new Error(data.error || 'Failed to change comment status');
      }

      await loadComments();
      notify.success(`Status changed to ${getWorkItemCommentTypeLabel(nextType).toLowerCase()}`);
    } catch (error: unknown) {
      console.error('Error changing comment visibility:', error);
      notify.error(getErrorMessage(error, 'Failed to change comment status'));
    } finally {
      setUpdatingVisibilityCommentId(null);
    }
  };

  const toggleCommentVisibility = async (comment: Comment) => {
    await changeCommentType(comment, comment.is_internal ? 'closing_details' : 'internal');
  };

  const deleteComment = async (comment: Comment) => {
    if (!canManageExistingComments) {
      return;
    }

    await confirm({
      title: 'Delete Detail',
      message: 'Are you sure you want to delete this detail? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const targetWorkItemId = comment.work_item_id ? String(comment.work_item_id) : String(workItemId);
          const response = await fetch(`/api/v1/work-items/${targetWorkItemId}/comments?comment_id=${comment.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`
            }
          });

          const data = await parseJsonResponse<{ success?: boolean; error?: string }>(response);

          if (!response.ok) {
            throw new Error(data?.error || 'Failed to delete comment');
          }

          if (!data || data.success !== false) {
            await loadComments();
            notify.success('Detail deleted');
          } else {
            throw new Error(data.error || 'Failed to delete comment');
          }
        } catch (error: unknown) {
          console.error('Error deleting comment:', error);
          notify.error(getErrorMessage(error, 'Failed to delete detail'));
        }
      }
    });
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleEdit = (comment: Comment) => {
    if (!canManageExistingComments) {
      return;
    }

    const itemized = comment.is_itemized ?? isItemizedCommentText(comment.comment_text);
    setIsItemized(itemized);
    setEditingComment(comment);
    setNewComment(
      itemized
        ? createItemizedCommentDraft(comment.comment_text)
        : createPlainCommentDraft(comment.comment_text)
    );
    setCommentType(resolveWorkItemCommentType(comment));
    setCommentCreatedAtInput(toDateTimeLocalValue(comment.created_at));
    if (composerPresentation === 'floating') {
      setComposerModalOpen(true);
    } else {
      textareaRef.current?.focus();
    }
  };

  const cancelEdit = useCallback(() => {
    setEditingComment(null);
    setIsItemized(true);
    setNewComment(createItemizedCommentDraft());
    setCommentType(getInitialCommentType());
    setCommentCreatedAtInput('');
    setComposerModalOpen(false);
  }, [getInitialCommentType]);

  useEffect(() => {
    if (!composerModalOpen || composerPresentation !== 'floating' || typeof window === 'undefined') {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (isListening || isAudioRecording) {
        return;
      }
      event.preventDefault();
      void stopSpeechToText(false, true);
      cancelEdit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    composerModalOpen,
    composerPresentation,
    isListening,
    isAudioRecording,
    stopSpeechToText,
    cancelEdit,
  ]);

  const syncMentionUiFromCursor = useCallback(
    (value: string, cursor: number, teamId: string | null, typeSelected: boolean, resetHighlight: boolean) => {
      const before = value.slice(0, cursor);
      const match = before.match(/@([^@\n]*)$/);
      if (match && teamId && typeSelected) {
        mentionRangeRef.current = { start: match.index! };
        setMentionMenuOpen(true);
        setMentionFilter(match[1]);
        if (resetHighlight) {
          setMentionHighlight(0);
        }
      } else {
        setMentionMenuOpen(false);
        mentionRangeRef.current = null;
      }
    },
    [],
  );

  const handleComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewComment(value);
    const cursor = typeof e.target.selectionStart === 'number' ? e.target.selectionStart : value.length;
    syncMentionUiFromCursor(value, cursor, resolvedMentionTeamId, hasSelectedCommentType, true);
  };

  const handleComposerCaretMove = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const t = e.currentTarget;
    syncMentionUiFromCursor(t.value, t.selectionStart ?? t.value.length, resolvedMentionTeamId, hasSelectedCommentType, false);
  };

  const applyMentionPick = useCallback(
    (user: { user_id: number; label: string }) => {
      const ta = textareaRef.current;
      const end = ta?.selectionEnd ?? newComment.length;
      const value = newComment;
      const before = value.slice(0, end);
      const match = before.match(/@([^@\n]*)$/);
      if (match === null || match.index === undefined) {
        setMentionMenuOpen(false);
        mentionRangeRef.current = null;
        return;
      }
      const newBefore = `${before.slice(0, match.index)}${formatWorkItemCommentMentionToken(user.user_id, user.label)} `;
      const after = value.slice(end);
      setNewComment(newBefore + after);
      setMentionMenuOpen(false);
      mentionRangeRef.current = null;
      window.setTimeout(() => {
        ta?.focus();
        const pos = newBefore.length;
        ta?.setSelectionRange(pos, pos);
      }, 0);
    },
    [newComment],
  );

  const handleAppendItem = () => {
    if (!hasSelectedCommentType) {
      return;
    }

    setNewComment((prev) =>
      isItemized ? appendItemizedCommentLine(prev) : appendPlainCommentLine(prev)
    );

    window.setTimeout(() => {
      textareaRef.current?.focus();
      if (textareaRef.current) {
        const end = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(end, end);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      submitComment();
      return;
    }

    if (mentionMenuOpen && resolvedMentionTeamId && hasSelectedCommentType) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionMenuOpen(false);
        mentionRangeRef.current = null;
        return;
      }

      if (filteredMentionSuggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionHighlight((i) => Math.min(i + 1, filteredMentionSuggestions.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionHighlight((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          applyMentionPick(filteredMentionSuggestions[mentionHighlight]);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        if (filteredMentionSuggestions.length > 0) {
          e.preventDefault();
          applyMentionPick(filteredMentionSuggestions[mentionHighlight]);
          return;
        }
        if (mentionMembersLoading) {
          e.preventDefault();
          return;
        }
        setMentionMenuOpen(false);
        mentionRangeRef.current = null;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      if (isItemized) {
        e.preventDefault();
        handleAppendItem();
      }
      // In plain-text mode let the textarea insert a newline naturally
    }
  };

  const pendingCommentText = isItemized
    ? serializeItemizedCommentDraft(newComment)
    : serializePlainCommentDraft(newComment);

  const isSpeechUiLocked = isListening || isSpeechStarting || isAudioRecording || isTranscribingAudio;
  const itemizedSpeechBaseText = isSpeechUiLocked ? (speechDraftBaseRef.current || newComment) : newComment;
  const nextItemizedSpeechNumber = Math.max(1, extractItemizedCommentLines(itemizedSpeechBaseText).length + 1);
  const canSubmitComment = canCreateComments && hasSelectedCommentType && !closingDetailsComposerLocked && Boolean(pendingCommentText) && !submitting && !isListening && !isSpeechStarting && !isAudioRecording && !isTranscribingAudio;
  const speechMicDisabled =
    !canCreateComments || !hasSelectedCommentType || submitting || isTranscribingAudio || isSpeechStarting;
  const speechToTextTitle = isAudioRecording
    ? 'Stop recording and transcribe'
    : isTranscribingAudio
      ? 'Transcribing audio'
      : isSpeechStarting
        ? 'Starting speech-to-text'
        : isCapacitor()
          ? isItemized
            ? `Record item ${nextItemizedSpeechNumber}`
            : 'Record comment'
          : speechRecognitionSupported
            ? isItemized
              ? `Use speech-to-text for item ${nextItemizedSpeechNumber}`
              : 'Use speech-to-text'
            : 'Tap to try speech-to-text';
  const inlineMicInTextarea = enableSpeechToText && !useCompactComposerLayout;
  const mentionHint = enableMentions && resolvedMentionTeamId ? ' • @ mentions team (Enter/Tab)' : '';
  const composerHelperText = useCompactComposerLayout
    ? isItemized
      ? `Enter adds the next numbered item${mentionHint}`
      : `Enter for new line${mentionHint}`
    : `Press Ctrl+Enter to submit • ${isItemized ? 'Enter adds the next numbered item' : 'Enter for new line'}${mentionHint}`;
  const getTypeButtonClasses = useCallback((nextType: WorkItemCommentType, isSelected: boolean) => {
    if (isSelected) {
      switch (nextType) {
        case 'internal':
          return isDark
            ? 'bg-yellow-500/22 text-yellow-100 ring-1 ring-yellow-400/65 border border-yellow-300/40 shadow-[0_4px_14px_rgba(250,204,21,0.18)]'
            : 'bg-yellow-100 text-yellow-950 ring-1 ring-yellow-300 border border-yellow-200 shadow-[0_4px_12px_rgba(234,179,8,0.16)]';
        case 'external':
          return isDark
            ? 'bg-emerald-500/22 text-emerald-100 ring-1 ring-emerald-400/65 border border-emerald-300/40 shadow-[0_4px_14px_rgba(16,185,129,0.18)]'
            : 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-300 border border-emerald-200 shadow-[0_4px_12px_rgba(16,185,129,0.16)]';
        case 'closing_details':
        default:
          return isDark
            ? 'bg-blue-500/22 text-blue-100 ring-1 ring-blue-400/65 border border-blue-300/40 shadow-[0_4px_14px_rgba(59,130,246,0.18)]'
            : 'bg-blue-100 text-blue-950 ring-1 ring-blue-300 border border-blue-200 shadow-[0_4px_12px_rgba(59,130,246,0.16)]';
      }
    }

    if (!hasSelectedCommentType) {
      switch (nextType) {
        case 'internal':
          return isDark
            ? 'text-yellow-100/95 border border-yellow-500/25 bg-yellow-500/10 hover:bg-yellow-500/18 hover:text-yellow-50'
            : 'text-yellow-800 border border-yellow-200 bg-white/80 hover:bg-yellow-50 hover:text-yellow-900';
        case 'external':
          return isDark
            ? 'text-emerald-100/95 border border-emerald-500/25 bg-emerald-500/10 hover:bg-emerald-500/18 hover:text-emerald-50'
            : 'text-emerald-800 border border-emerald-200 bg-white/80 hover:bg-emerald-50 hover:text-emerald-900';
        case 'closing_details':
        default:
          return isDark
            ? 'text-blue-100/95 border border-blue-500/25 bg-blue-500/10 hover:bg-blue-500/18 hover:text-white'
            : 'text-blue-700 border border-blue-200 bg-white/80 hover:bg-blue-50 hover:text-blue-900';
      }
    }

    return isDark
      ? 'text-gray-300 border border-transparent hover:bg-gray-800/80 hover:text-white'
      : 'text-gray-600 border border-transparent hover:bg-white hover:text-gray-900';
  }, [hasSelectedCommentType, isDark]);
  const commentTypeSelectorClassName = shouldShowCommentTypeSelector
    ? hasSelectedCommentType
      ? isDark
        ? 'bg-gray-900/70 ring-1 ring-gray-600/80'
        : 'bg-gray-100 ring-1 ring-gray-200'
      : isDark
        ? 'bg-blue-950/40 ring-2 ring-blue-500/70 shadow-[0_0_0_1px_rgba(59,130,246,0.25)]'
        : 'bg-blue-50 ring-2 ring-blue-300 shadow-[0_0_0_1px_rgba(147,197,253,0.75)]'
    : '';

  const renderComposerCard = (showAvatar = true): React.ReactElement => (
    <div className={`rounded-lg border p-3 sm:p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
      {createOnlyMode && (
        <div className={`mb-3 rounded-lg border px-3 py-2 ${isDark ? 'border-amber-800 bg-amber-900/15 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-700'
          }`}>
          <p className="text-sm font-semibold">Existing comments are locked</p>
          <p className="mt-1 text-xs leading-relaxed">
            {resolvedCreateOnlyReason}
          </p>
        </div>
      )}

      {enableExternalCommentType && closingDetailsLocked && (
        <div className={`mb-3 rounded-lg border px-3 py-2 ${isDark ? 'border-purple-800 bg-purple-900/15 text-purple-200' : 'border-purple-200 bg-purple-50 text-purple-700'
          }`}>
          <p className="text-sm font-semibold">Closing details are locked</p>
          <p className="mt-1 text-xs leading-relaxed">
            {resolvedClosingDetailsLockedReason} You can still add internal or external comments, but you cannot convert them into closing details afterwards.
          </p>
        </div>
      )}

      {shouldShowCommentTypeSelector && !hasSelectedCommentType && (
        <div className={`mb-3 rounded-lg border px-3 py-2 ${isDark ? 'border-blue-800 bg-blue-900/15 text-blue-200' : 'border-blue-200 bg-blue-50 text-blue-700'
          }`}>
          <p className="text-sm font-semibold">Choose a comment type</p>
          <p className="mt-1 text-xs leading-relaxed">
            Select whether this note is a closing detail, customer-facing comment, or internal note before typing.
          </p>
        </div>
      )}

      {editingComment && (
        <div className={`mb-3 pb-3 border-b flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {`Editing ${getUserDisplayName({ displayName: editingComment.user_display_name || editingComment.user_name, email: editingComment.user_email })}'s details...`}
          </span>
          <button
            onClick={cancelEdit}
            disabled={isSpeechUiLocked}
            className={`text-xs px-2 py-1 rounded ${isDark
              ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Cancel
          </button>
        </div>
      )}

      {enableSpeechToText && (isListening || isSpeechStarting || isAudioRecording || isTranscribingAudio) && (
        <div className={`mb-3 flex items-center gap-2 rounded-lg border px-3 py-2 ${isAudioRecording || isListening
          ? isDark
            ? 'border-red-800 bg-red-900/20 text-red-300'
            : 'border-red-200 bg-red-50 text-red-600'
          : isDark
            ? 'border-amber-800 bg-amber-900/20 text-amber-200'
            : 'border-amber-200 bg-amber-50 text-amber-700'
          }`}>
          {isAudioRecording || isListening ? (
            <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          ) : (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          )}
          <span className="text-xs font-medium">
            {isAudioRecording
              ? isItemized
                ? `Recording item ${nextItemizedSpeechNumber} for transcription… ${audioRecordingDuration}s`
                : `Recording comment for transcription… ${audioRecordingDuration}s`
              : isTranscribingAudio
                ? isItemized
                  ? `Transcribing item ${nextItemizedSpeechNumber}…`
                  : 'Transcribing comment…'
                : isListening
                  ? 'Listening… speak now to fill in the comment'
                  : 'Starting speech-to-text…'}
          </span>
          {(isListening || isAudioRecording) && (
            <button
              type="button"
              onClick={() => stopSpeechToText()}
              className={`ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${isDark
                ? 'bg-red-900/40 text-red-200 hover:bg-red-900/60'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
            >
              <MicOff size={12} />
              {isAudioRecording ? 'Stop & transcribe' : 'Stop'}
            </button>
          )}
        </div>
      )}

      <div className={`flex items-start ${useCompactComposerLayout ? 'gap-2.5' : 'gap-3'}`}>
        {showAvatar && (
          <UserAvatar
            user={{
              userId: currentUserId,
              firstName: user?.first_name,
              lastName: user?.last_name,
              email: user?.email,
              displayName: getUserDisplayName({
                firstName: user?.first_name,
                lastName: user?.last_name,
                email: user?.email,
              }),
            }}
            size={useCompactComposerLayout ? 'sm' : 'md'}
          />
        )}

        <div className={`flex-1 min-w-0 flex flex-col gap-2 ${showAvatar ? '' : 'w-full'}`}>
          {shouldShowCommentTypeSelector && (
            enableExternalCommentType ? (
              <div
                className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-900"
                role="tablist"
                aria-label="Comment type"
              >
                {commentTypeOptions.map(({ type: nextType, label, compactLabel, icon: Icon }) => {
                  const nextTypeIsLocked = closingDetailsLocked && nextType === 'closing_details';
                  const isSelected = commentType === nextType;

                  return (
                    <button
                      key={nextType}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      onClick={() => setCommentType(nextType)}
                      disabled={isSpeechUiLocked || nextTypeIsLocked}
                      className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected
                          ? nextType === 'internal'
                            ? 'bg-yellow-100 text-yellow-900 ring-1 ring-yellow-200 dark:bg-yellow-500/20 dark:text-yellow-300 dark:ring-yellow-500/30'
                            : nextType === 'external'
                              ? 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-500/30'
                              : 'bg-blue-100 text-blue-900 ring-1 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:ring-blue-500/30'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                      }`}
                      title={label}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="text-center leading-tight line-clamp-1">
                        {useCompactComposerLayout ? compactLabel : label}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex justify-stretch">
                <button
                  type="button"
                  onClick={() => setCommentType(isInternal ? 'closing_details' : 'internal')}
                  disabled={isSpeechUiLocked}
                  className={`w-full flex items-center justify-center gap-2 rounded-xl py-2.5 px-3 text-xs font-semibold transition-colors ${isInternal
                    ? isDark
                      ? 'bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/30'
                      : 'bg-yellow-100 text-yellow-900 ring-1 ring-yellow-200'
                    : isDark
                      ? 'bg-gray-700 text-gray-300 ring-1 ring-gray-600 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isInternal ? <Lock size={14} /> : <Globe size={14} />}
                  {isInternal ? 'Internal' : 'Visible to Customer'}
                </button>
              </div>
            )
          )}

          {hasSelectedCommentType && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  if (!isItemized) {
                    setNewComment(createItemizedCommentDraft(newComment));
                  }
                  setIsItemized(true);
                }}
                disabled={isSpeechUiLocked}
                title="Numbered list"
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${isItemized
                  ? isDark
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-100 text-blue-700'
                  : isDark
                    ? 'text-gray-400 hover:bg-gray-700'
                    : 'text-gray-500 hover:bg-gray-100'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <ListOrdered size={12} />
                Numbered
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isItemized) {
                    setNewComment(extractItemizedCommentLines(newComment).join('\n'));
                  }
                  setIsItemized(false);
                }}
                disabled={isSpeechUiLocked}
                title="Plain text"
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${!isItemized
                  ? isDark
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-100 text-blue-700'
                  : isDark
                    ? 'text-gray-400 hover:bg-gray-700'
                    : 'text-gray-500 hover:bg-gray-100'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <AlignLeft size={12} />
                Plain
              </button>
            </div>
          )}

          <div
            className={`relative rounded-[1.35rem] border ${isDark ? 'border-gray-600 bg-gray-700/90' : 'border-gray-300 bg-gray-50'
              } shadow-sm focus-within:ring-2 focus-within:ring-blue-500/80 focus-within:border-blue-500`}
          >
            {mentionMenuOpen && resolvedMentionTeamId && hasSelectedCommentType ? (
              <div
                className={`absolute bottom-full left-2 right-2 mb-1 max-h-48 overflow-y-auto rounded-lg border shadow-lg z-30 py-1 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}
              >
                {mentionMembersLoading ? (
                  <div className={`px-3 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading team members…
                  </div>
                ) : filteredMentionSuggestions.length === 0 ? (
                  <div className={`px-3 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {mentionCandidates.length === 0 ? 'No teammates on this team.' : 'No matching teammates.'}
                  </div>
                ) : (
                  filteredMentionSuggestions.map((m, idx) => (
                    <button
                      key={m.user_id}
                      type="button"
                      onMouseDown={(ev) => ev.preventDefault()}
                      onClick={() => applyMentionPick(m)}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${idx === mentionHighlight
                        ? isDark
                          ? 'bg-gray-700 text-white'
                          : 'bg-blue-50 text-blue-900'
                        : isDark
                          ? 'text-gray-200 hover:bg-gray-700/80'
                          : 'text-gray-800 hover:bg-gray-50'
                        }`}
                    >
                      <span className="truncate">{m.label}</span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
            <textarea
              ref={textareaRef}
              value={newComment}
              onChange={handleComposerChange}
              onSelect={handleComposerCaretMove}
              onClick={handleComposerCaretMove}
              onKeyUp={handleComposerCaretMove}
              onKeyDown={handleKeyDown}
              placeholder={commentPlaceholder}
              rows={useCompactComposerLayout ? 3 : 2}
              className={`w-full ${useCompactComposerLayout ? 'min-h-[4.25rem]' : 'min-h-[3rem]'} rounded-[1.35rem] border-0 bg-transparent pl-3 ${inlineMicInTextarea ? 'pr-14' : 'pr-3'} py-2.5 text-sm resize-none ${isDark ? 'text-gray-200 placeholder-gray-400' : 'text-gray-900 placeholder-gray-400'
                } focus:ring-0 focus:outline-none`}
              disabled={submitting || isSpeechUiLocked || !hasSelectedCommentType}
            />
            {inlineMicInTextarea && (
              <button
                type="button"
                onClick={() => void startSpeechToText()}
                disabled={speechMicDisabled}
                title={speechToTextTitle}
                aria-label={speechToTextTitle}
                className={`absolute right-1.5 bottom-1.5 flex h-10 w-10 items-center justify-center rounded-full transition-colors ${isListening || isAudioRecording
                  ? isDark
                    ? 'bg-red-900/50 text-red-200 hover:bg-red-900/70'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                  : isTranscribingAudio
                    ? isDark
                      ? 'bg-amber-900/40 text-amber-200'
                      : 'bg-amber-100 text-amber-800'
                    : speechMicDisabled
                      ? isDark
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-300 cursor-not-allowed'
                      : speechRecognitionSupported
                        ? isDark
                          ? 'text-gray-300 hover:bg-gray-600/60'
                          : 'text-gray-600 hover:bg-gray-200/90'
                        : isDark
                          ? 'text-amber-300/90 hover:bg-amber-900/30'
                          : 'text-amber-700 hover:bg-amber-100'
                  }`}
              >
                {isSpeechStarting || isTranscribingAudio ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : isListening || isAudioRecording ? (
                  <MicOff className="h-5 w-5" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>
            )}
          </div>

          <div className={useCompactComposerLayout ? 'grid grid-cols-[auto_auto_minmax(0,1fr)] gap-2 items-stretch' : 'flex items-center justify-between gap-2 flex-wrap'}>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleAppendItem}
              disabled={isSpeechUiLocked || !hasSelectedCommentType}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${useCompactComposerLayout ? 'justify-center whitespace-nowrap' : ''} ${isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Plus size={12} />
              {isItemized ? 'Add item' : 'New line'}
            </button>

            {enableSpeechToText && useCompactComposerLayout && (
              <button
                type="button"
                onClick={() => void startSpeechToText()}
                disabled={speechMicDisabled}
                title={speechToTextTitle}
                aria-label={speechToTextTitle}
                className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-lg transition-colors ${isListening || isAudioRecording
                  ? isDark
                    ? 'bg-red-900/50 text-red-200 hover:bg-red-900/70'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                  : isTranscribingAudio
                    ? isDark
                      ? 'bg-amber-900/40 text-amber-200'
                      : 'bg-amber-100 text-amber-800'
                    : speechMicDisabled
                      ? isDark
                        ? 'bg-gray-700 text-gray-600 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : speechRecognitionSupported
                        ? isDark
                          ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : isDark
                          ? 'bg-amber-900/20 text-amber-300 hover:bg-amber-900/30'
                          : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  }`}
              >
                {isSpeechStarting || isTranscribingAudio ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isListening || isAudioRecording ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </button>
            )}

            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={submitComment}
              disabled={!canSubmitComment}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${useCompactComposerLayout ? 'w-full justify-center min-w-0' : ''} ${canSubmitComment
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : isDark
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              Save
            </button>
          </div>

          {closingDetailsComposerLocked && (
            <p className={`text-xs mt-2 ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
              {resolvedClosingDetailsLockedReason}
            </p>
          )}

          {editingComment && canModerateComments && (
            <div className="mt-1 sm:mt-3">
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Detail date & time
              </label>
              <input
                type="datetime-local"
                value={commentCreatedAtInput}
                onChange={(e) => setCommentCreatedAtInput(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark
                  ? 'bg-gray-700 border-gray-600 text-gray-200'
                  : 'bg-gray-50 border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                This controls the displayed created date and time for this comment and is logged in the activity history.
              </p>
            </div>
          )}

          <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {hasSelectedCommentType ? composerHelperText : 'Select a comment type to enable the editor'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`space-y-4${isNativeApp ? ' pb-[max(12px,env(safe-area-inset-bottom,0px))]' : ''}`}
      data-tour-id="work-item-comments-section"
    >
      <AppointmentCompletionRequirementOverrideCard
        workItem={appointmentWorkItem}
        appointmentTypeOptions={appointmentTypeOptions}
        requirementKey="public_comments"
        title={enableExternalCommentType ? 'Closing details' : 'Public closing comments'}
        description={enableExternalCommentType
          ? 'Use this only when customer-facing closing details genuinely cannot be captured for this appointment.'
          : 'Use this only when customer-facing closing comments genuinely cannot be captured for this appointment.'}
        placeholder={enableExternalCommentType
          ? 'Explain why closing details can be bypassed for this appointment...'
          : 'Explain why public closing comments can be bypassed for this appointment...'}
        disabled={!canManageExistingComments}
        onChange={onAppointmentRequirementOverrideChange || (() => { })}
      />

      {commentsReadOnly ? (
        <div className={`rounded-lg border p-3 sm:p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 rounded-full p-2 ${isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
              }`}>
              <Lock size={16} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                Comments are read-only
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {resolvedReadOnlyReason}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {composerPresentation === 'inline' && renderComposerCard()}
        </>
      )}

      {!commentsReadOnly && composerPresentation === 'floating' && (
        composerTriggerVariant === 'full-button' ? (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => setComposerModalOpen(true)}
              disabled={!canCreateComments}
              title={canCreateComments ? 'Write a comment' : 'You cannot add comments'}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-blue-300 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Plus className="w-5 h-5" />
              New Comment
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setComposerModalOpen(true)}
              disabled={!canCreateComments}
              title={canCreateComments ? 'Write a comment' : 'You cannot add comments'}
              aria-label={canCreateComments ? 'Write a comment' : 'You cannot add comments'}
              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition ${isDark ? 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <SquarePen className="h-4 w-4" />
            </button>
          </div>
        )
      )}


      {composerPresentation === 'floating' && composerModalOpen && typeof document !== 'undefined' && createPortal(
        <div
          className={`fixed inset-0 ${COMMENT_OVERLAY_Z_CLASS} flex items-stretch justify-center sm:items-center p-0 sm:p-4`}
          role="dialog"
          aria-modal="true"
          aria-label={editingComment ? 'Edit comment' : 'New comment'}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close"
            onClick={() => {
              if (isListening || isAudioRecording) return;
              void stopSpeechToText(false, true);
              setComposerModalOpen(false);
              if (editingComment) {
                setEditingComment(null);
                setIsItemized(true);
                setNewComment(createItemizedCommentDraft());
                setCommentType(getInitialCommentType());
                setCommentCreatedAtInput('');
              }
            }}
          />
          <div
            className={`relative flex w-full max-w-none flex-col overflow-hidden min-h-0 rounded-none border shadow-2xl sm:max-w-lg sm:max-h-[min(92vh,44rem)] sm:max-h-[min(92dvh,44rem)] sm:rounded-2xl ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
              }`}
            style={{
              paddingTop: isCapacitor() ? 'env(safe-area-inset-top)' : undefined,
              paddingBottom: isCapacitor() ? 'max(12px, env(safe-area-inset-bottom))' : undefined,
            }}
          >
            {/* Sticky Header — matches SmartTaskModalWrapper pattern (blue icon + bold title) */}
            <div className="shadow-sm flex-shrink-0 bg-white dark:bg-gray-800 transition-[padding] duration-200">
              <div className="max-w-4xl mx-auto px-3 py-3 relative">
                <button
                  type="button"
                  disabled={submitting || isListening || isAudioRecording}
                  onClick={() => {
                    if (isListening || isAudioRecording) return;
                    void stopSpeechToText(false, true);
                    setComposerModalOpen(false);
                    if (editingComment) {
                      setEditingComment(null);
                      setIsItemized(true);
                      setNewComment(createItemizedCommentDraft());
                      setCommentType(getInitialCommentType());
                      setCommentCreatedAtInput('');
                    }
                  }}
                  className="absolute right-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="text-center pt-1">
                  <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-14 sm:w-14">
                    <MessageSquare className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
                    {editingComment ? 'Edit Comment' : 'New Comment'} <UserDocsLink docsPath="/total-operations/workflows/service-operations/comments-collaboration" label="User guide: Comments & collaboration" className="ml-1 align-middle" />
                  </h1>
                </div>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {renderComposerCard(false)}
            </div>
          </div>
        </div>,
        document.body,
      )}


      {/* Portal: capped inner scroll. Native: flat list — tab body scrolls once so the composer is reachable. */}
      {visibleComments.length > 0 ? (
        <div
          className={`rounded-lg border divide-y ${isNativeApp ? '' : 'overflow-y-auto overscroll-y-contain min-h-0 max-h-[min(55vh,36rem)] '
            }${isDark
              ? 'bg-gray-800 border-gray-700 divide-gray-700'
              : 'bg-white border-gray-200 divide-gray-100'
            }`}
        >
          {visibleComments.map(comment => (
            <div key={comment.id} className="px-3 sm:px-4">
              <CommentItem
                comment={comment}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={deleteComment}
                onToggleVisibility={toggleCommentVisibility}
                onChangeType={changeCommentType}
                isDark={isDark}
                canModerateComments={canModerateComments}
                canToggleVisibility={canToggleCommentVisibility}
                enableExternalCommentType={enableExternalCommentType}
                closingDetailsLocked={closingDetailsLocked}
                readOnly={!canManageExistingComments}
                isInteractive={interactiveWorkItemIdSet.has(String(comment.work_item_id))}
                changingVisibilityCommentId={updatingVisibilityCommentId}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className={`text-center py-5 sm:py-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          <MessageSquare className={`w-9 h-9 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'
            }`} />
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {restrictToClosingDetails || !enableExternalCommentType ? 'No closing details yet' : 'No comments yet'}
          </p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {restrictToClosingDetails
              ? 'Add the first closing detail.'
              : enableExternalCommentType
                ? 'Add the first closing detail, external comment, or internal comment.'
                : 'Add the first closing detail.'}
          </p>
        </div>
      )}
    </div>
  );
}
